/**
 * Review criteria version history for a task type — tracks when questions/checks change.
 */

import { useEffect, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faListCheck,
  faTag,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { Button } from "@/components/ui/button/Button";
import { agentReviewAPI, type CriteriaVersionRecord } from "@/services/api/agentReview";

interface CriteriaVersionsPanelProps {
  catalogueKey: string | null;
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function CriteriaVersionsPanel({ catalogueKey }: CriteriaVersionsPanelProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<CriteriaVersionRecord[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [publishLabel, setPublishLabel] = useState("");

  const load = async () => {
    if (!catalogueKey) return;
    setLoading(true);
    setError(null);
    try {
      setVersions(await agentReviewAPI.listCriteriaVersions(catalogueKey));
    } catch {
      setError("Couldn't load criteria versions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !catalogueKey) return;
    void load();
  }, [open, catalogueKey]);

  const handlePublish = async () => {
    if (!catalogueKey || !publishLabel.trim()) return;
    setPublishing(true);
    setError(null);
    try {
      await agentReviewAPI.publishCriteriaVersion(catalogueKey, publishLabel.trim());
      setPublishLabel("");
      await load();
    } catch {
      setError("Couldn't publish criteria version.");
    } finally {
      setPublishing(false);
    }
  };

  if (!catalogueKey) return null;

  const current = versions.find((v) => v.is_current);

  return (
    <div className="rounded-xl ring-1 ring-white/10 bg-white/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02]"
      >
        <FontAwesomeIcon icon={faListCheck} className="text-slate-400 text-sm shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-200">Review criteria versions</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {current ? `Current: ${current.label}` : "Track when review questions change"}
          </p>
        </div>
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronRight}
          className="text-slate-500 text-xs shrink-0"
        />
      </button>

      {open ? (
        <div className="border-t border-white/5 px-4 py-3 space-y-3">
          {loading ? <LoadingPlaceholder text="Loading criteria versions…" /> : null}
          {error ? <p className="text-sm text-rose-400">{error}</p> : null}

          {!loading && versions.length === 0 ? (
            <p className="text-sm text-slate-500">
              No criteria versions yet — they are created automatically when checks change or when
              you publish a labelled version.
            </p>
          ) : null}

          {!loading
            ? versions.map((version) => (
                <div
                  key={version.criteria_version_id}
                  className={clsx(
                    "rounded-lg px-3 py-2.5 ring-1 space-y-0.5",
                    version.is_current
                      ? "bg-indigo-500/[0.06] ring-indigo-500/20"
                      : "bg-white/[0.02] ring-white/5",
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-slate-200">{version.label}</span>
                    {version.is_current ? (
                      <span className="rounded-full bg-indigo-500/15 px-2 py-0.5 text-[10px] font-medium text-indigo-200 ring-1 ring-indigo-500/25">
                        Current
                      </span>
                    ) : null}
                    <span className="text-[11px] text-slate-500 ml-auto tabular-nums">
                      v{version.version_number}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {formatWhen(version.created_at)} · {version.created_by}
                  </p>
                </div>
              ))
            : null}

          <div className="rounded-lg bg-white/[0.02] px-3 py-3 ring-1 ring-white/5 space-y-2">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <FontAwesomeIcon icon={faTag} className="text-[10px]" />
              Publish labelled version (current checks)
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                value={publishLabel}
                onChange={(e) => setPublishLabel(e.target.value)}
                placeholder="e.g. After orch check tweak — Jun 2026"
                className="min-w-[12rem] flex-1 rounded-lg bg-slate-900/60 px-3 py-1.5 text-sm text-slate-200 ring-1 ring-white/10 placeholder:text-slate-600 focus:outline-none focus:ring-indigo-500/40"
              />
              <Button
                color="indigo"
                className="!text-xs"
                disabled={!publishLabel.trim() || publishing}
                onClick={() => void handlePublish()}
              >
                {publishing ? "Publishing…" : "Publish"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
